import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronRight, Paperclip } from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { StorageLink } from "../misc/storage";

export type EmailRecord = {
  id: number;
  direction: "in" | "out";
  subject: string | null;
  from_address: string | null;
  from_name: string | null;
  to_addresses: string[];
  cc_addresses: string[];
  sent_at: string | null;
  snippet: string | null;
  body_text: string | null;
  attachments: { name: string; mime: string; size: number; path: string | null }[];
  headers_only: boolean;
  review_status: string;
  company_id: number | null;
  contact_id: number | null;
  deal_id: number | null;
};

export const EMAIL_COLUMNS =
  "id,direction,subject,from_address,from_name,to_addresses,cc_addresses,sent_at,snippet,body_text,attachments,headers_only,review_status,company_id,contact_id,deal_id";

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";


export type KnownContact = { id: number; name: string };

// LDF: Alle E-Mail-Adressen der Kontakte (für den grünen Punkt „schon als Kontakt angelegt“)
export const useContactsByEmail = () =>
  useQuery({
    queryKey: ["contacts-by-email"],
    queryFn: async () => {
      const map = new Map<string, KnownContact>();
      for (let from = 0; ; from += 1000) {
        const { data, error } = await getSupabaseClient()
          .from("contacts")
          .select("id,first_name,last_name,email_jsonb")
          .neq("email_jsonb", "[]")
          .order("id")
          .range(from, from + 999);
        if (error) throw error;
        for (const c of data ?? []) {
          const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
          for (const e of (c.email_jsonb ?? []) as { email?: string }[]) {
            if (e.email) map.set(e.email.toLowerCase(), { id: c.id, name });
          }
        }
        if (!data || data.length < 1000) break;
      }
      return map;
    },
    staleTime: 60_000,
  });

const KnownDot = ({ contact }: { contact?: KnownContact }) =>
  contact ? (
    <span
      className="mr-1 inline-block h-2 w-2 shrink-0 rounded-full bg-green-600 align-middle"
      title={`Schon als Kontakt angelegt: ${contact.name}`}
      aria-label="Kontakt vorhanden"
    />
  ) : null;

export const EmailItem = ({
  email,
  actions,
  contactsByEmail,
  open: controlledOpen,
  onToggle,
}: {
  email: EmailRecord;
  actions?: React.ReactNode;
  contactsByEmail?: Map<string, KnownContact>;
  open?: boolean;
  onToggle?: () => void;
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = (value: boolean) => (onToggle ? onToggle() : setLocalOpen(value));
  const incoming = email.direction === "in";
  const people = incoming
    ? [{ address: email.from_address ?? "", label: email.from_name ? `${email.from_name} <${email.from_address}>` : email.from_address ?? "" }]
    : [...(email.to_addresses ?? []), ...(email.cc_addresses ?? [])].map((a) => ({ address: a, label: a }));
  const known = contactsByEmail
    ? [...new Map([email.from_address, ...(email.to_addresses ?? []), ...(email.cc_addresses ?? [])]
        .map((a) => contactsByEmail.get(String(a ?? "").toLowerCase()))
        .filter((c): c is KnownContact => !!c)
        .map((c) => [c.id, c])).values()]
    : [];

  return (
    <div id={`email-${email.id}`} className={`border-b border-border py-3 ${open && onToggle ? "scroll-mt-20" : ""}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-3 text-left cursor-pointer"
      >
        <span
          className={`mt-0.5 rounded-full p-1 ${incoming ? "bg-secondary text-secondary-foreground" : "bg-primary/15 text-primary"}`}
          title={incoming ? "Eingang" : "Ausgang"}
        >
          {incoming ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium">{email.subject || "(ohne Betreff)"}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatDate(email.sent_at)}</span>
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {incoming ? "Von " : "An "}
            {people.map((p, i) => (
              <Fragment key={`${p.address}-${i}`}>
                {i > 0 && ", "}
                <KnownDot contact={contactsByEmail?.get(p.address.toLowerCase())} />
                {p.label}
              </Fragment>
            ))}
          </div>
          {!open && email.snippet && (
            <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{email.snippet}</div>
          )}
          {!open && email.headers_only && (
            <div className="mt-1 text-xs italic text-muted-foreground">
              Nur Kopfzeilen (privates Postfach) – Text wird nach Zuordnung nachgeladen
            </div>
          )}
        </div>
        <span className="flex items-center gap-1 text-muted-foreground">
          {email.attachments?.length ? <Paperclip className="h-3.5 w-3.5" /> : null}
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>

      {open && (
        <div className="ml-9 mt-3 space-y-3">
          {known.length ? (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">Kontakte im CRM:</span>
              {known.map((c) => (
                <Link key={c.id} to={`/contacts/${c.id}/show`} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 hover:bg-muted">
                  <KnownDot contact={c} />
                  {c.name || "Kontakt"}
                </Link>
              ))}
            </div>
          ) : null}
          {email.cc_addresses?.length ? (
            <div className="text-xs text-muted-foreground">CC: {email.cc_addresses.join(", ")}</div>
          ) : null}
          {email.attachments?.length ? (
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((a) =>
                a.path ? (
                  <StorageLink
                    key={a.path}
                    file={{ path: a.path }}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs hover:bg-muted"
                  >
                    <Paperclip className="h-3 w-3" />
                    {a.name}
                    <span className="text-muted-foreground">({Math.round(a.size / 1024)} KB)</span>
                  </StorageLink>
                ) : (
                  <span key={a.name} className="text-xs text-muted-foreground">
                    {a.name} (nicht gespeichert)
                  </span>
                ),
              )}
            </div>
          ) : null}
          <div className="max-h-[480px] overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm leading-6">
            {email.body_text || "(kein Text)"}
          </div>
          {actions}
        </div>
      )}
    </div>
  );
};

type EmailScope =
  | { type: "company"; id: number | string }
  | { type: "contact"; id: number | string; addresses: string[] }
  | { type: "deal"; id: number | string; companyId?: number | string | null };

export const useEmails = (scope: EmailScope) =>
  useQuery({
    queryKey: ["emails", scope],
    queryFn: async () => {
      let query = getSupabaseClient()
        .from("emails")
        .select(EMAIL_COLUMNS)
        .neq("review_status", "ignoriert")
        .order("sent_at", { ascending: false })
        .limit(300);
      if (scope.type === "company") {
        query = query.eq("company_id", scope.id);
      } else if (scope.type === "contact") {
        const quoted = scope.addresses.map((a) => `"${a.toLowerCase()}"`).join(",");
        query = quoted
          ? query.or(
              `contact_id.eq.${scope.id},from_address.in.(${quoted}),to_addresses.ov.{${quoted}},cc_addresses.ov.{${quoted}}`,
            )
          : query.eq("contact_id", scope.id);
      } else {
        query = scope.companyId
          ? query.or(`deal_id.eq.${scope.id},company_id.eq.${scope.companyId}`)
          : query.eq("deal_id", scope.id);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as EmailRecord[];
    },
  });

export const EmailList = ({ scope, emptyText }: { scope: EmailScope; emptyText?: string }) => {
  const { data, isPending, error } = useEmails(scope);
  if (isPending) return <p className="py-6 text-sm text-muted-foreground">E-Mails werden geladen …</p>;
  if (error) return <p className="py-6 text-sm text-destructive">E-Mails konnten nicht geladen werden.</p>;
  if (!data?.length)
    return <p className="py-6 text-sm text-muted-foreground">{emptyText ?? "Noch keine E-Mails vorhanden."}</p>;
  return (
    <div>
      <p className="pb-1 text-xs text-muted-foreground">
        {data.length} E-Mail{data.length === 1 ? "" : "s"} · neueste zuerst
      </p>
      {data.map((email) => (
        <EmailItem key={email.id} email={email} />
      ))}
    </div>
  );
};
